import { buildCssClassName, getTransitionTwClasses } from "@/3514/utils"

import { ESelectSize, ESelectVariant } from "./select.types"

const baseClasses = {
    container: ({ size }: { size: ESelectSize }): string =>
        buildCssClassName(
            "flex items-center flex-col md:flex-row gap-y-[10px] md:gap-y-[0px] md:gap-x-[20px]",
            size === ESelectSize.Full ? "w-full" : "w-auto"
        ),
    wrapper: (): string => "relative h-full",
    optionText: ({ isDisabled, isError }: { isDisabled: boolean; isError: boolean }): string =>
        buildCssClassName("select-none pointer-events-none line-clamp-1 truncate", {
            "group-focus:text-blue-200 group-focus:font-[800]": !isDisabled && !isError
        }),
    emptyOptionsText: (): string => "block mx-[20px] my-[5px]",
    spinner: (): string => "my-[20px]"
}

const variantClassesMap: {
    [V in ESelectVariant]: {
        wrapper(params: { isOpen: boolean; isDisabled: boolean; isError: boolean }): string
        container(params: { isFixedPosition: boolean; isError: boolean }): string
        option(params: { wasSelected: boolean; isActive: boolean; isDisabled: boolean }): string
    }
} = {
    [ESelectVariant.Default]: {
        wrapper: ({
            isOpen,
            isDisabled,
            isError
        }: {
            isOpen: boolean
            isDisabled: boolean
            isError: boolean
        }): string =>
            buildCssClassName(
                ["flex", "items-center", "justify-between", "border-[1px]", "px-[12px]", "py-[7px]", "h-full"],
                getTransitionTwClasses(),
                [isDisabled ? "!cursor-not-allowed" : "cursor-pointer"],
                [
                    isOpen
                        ? [
                              isError ? "border-red-100" : "border-blue-400",
                              "rounded-tl-[8px] rounded-tr-[8px] rounded-bl-[0px] rounded-br-[0px]"
                          ]
                        : [
                              "rounded-[8px]",
                              isDisabled
                                  ? "border-blue-300 bg-white"
                                  : [
                                        "bg-white",
                                        [
                                            isError
                                                ? "border-red-100 hover:bg-red-500 focus:bg-red-500"
                                                : "border-blue-400 hover:bg-blue-600 focus:bg-blue-600"
                                        ]
                                    ]
                          ]
                ]
            ),
        container: ({ isFixedPosition, isError }: { isFixedPosition: boolean; isError: boolean }): string =>
            buildCssClassName([
                { "absolute z-10": !isFixedPosition },
                { "fixed overflow-y-scroll scrollbar-thin-5 z-[9999]": isFixedPosition },
                "p-[8px]",
                "mt-[0px]",
                "bg-white",
                "border-[1px]",
                { "border-b-blue-400 border-r-blue-400 border-l-blue-400": !isError },
                { "border-b-red-100 border-r-red-100 border-l-red-100": isError },
                "!border-t-transparent",
                "rounded-tl-[0px]",
                "rounded-tr-[0px]",
                "rounded-bl-[8px]",
                "rounded-br-[8px]",
                getTransitionTwClasses("all", 200)
            ]),
        option: ({
            wasSelected,
            isActive,
            isDisabled
        }: {
            wasSelected: boolean
            isActive: boolean
            isDisabled: boolean
        }): string =>
            buildCssClassName(
                ["px-[8px]", "py-[8px]", "max-h-[38px]", "border-b-[1px]", "last:border-transparent", "group"],
                getTransitionTwClasses("all", 200),
                isDisabled
                    ? "!cursor-not-allowed bg-gray-500 border-gray-400"
                    : [
                          [isActive || wasSelected ? "bg-blue-500" : "bg-white"],
                          "cursor-pointer",
                          "border-blue-500",
                          "hover:bg-blue-600",
                          "focus:bg-blue-500",
                          "focus:border-blue-400",
                          "focus:border-t-[1px]",
                          "focus:border-b-[1px]",
                          "focus:border-r-[1px]",
                          "focus:border-l-[1px]"
                      ]
            )
    }
}

const sizeClassesMap: { [S in ESelectSize]: { wrapper(): string; container(): string } } = {
    [ESelectSize._210]: {
        wrapper: (): string => "max-w-[210px] w-full",
        container: (): string => "max-w-[210px] w-full"
    },
    [ESelectSize.Medium]: {
        wrapper: (): string => "max-w-[300px] w-full",
        container: (): string => "max-w-[300px] w-full"
    },
    [ESelectSize.Full]: { wrapper: (): string => "w-full", container: (): string => "w-full" }
}

export default { baseClasses, variantClassesMap, sizeClassesMap }
